import { Input } from "reactstrap";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatMetricName } from "src/helpers/commonHelper";
import { useCustomizeColumns } from "./useCustomizeColumns";

interface CustomizeColumnsProps {
  tabId: string;
  exchangeId: string | number;
  isLoading?: boolean;
  metricLabels?: Record<string, string>;
  defaultMetrics?: string[];
}

const CustomizeColumns = ({ tabId, exchangeId, isLoading, metricLabels, defaultMetrics = [] }: CustomizeColumnsProps) => {
  const {
    selectedMetrics,
    permittedMetrics,
    metricOrder,
    dropdownOpen,
    searchTerm,
    dropdownMaxHeight,
    dropdownRef,
    buttonRef,
    toggleColumn,
    selectAll,
    clearAll,
    resetToDefaults,
    setDropdownOpen,
    handleSearchChange,
    handleDragEnd,
    calculateDropdownHeight,
    areAllSelected,
    filteredMetricsList,
  } = useCustomizeColumns({ tabId, exchangeId, defaultMetrics });

  // Defined inside the parent (same pattern as ExchangeTable's SortableColumnItem)
  // so that React replaces the DOM nodes after onDragEnd, causing the browser's
  // spurious post-drag click to land on a detached node React no longer tracks —
  // preventing the checkbox from toggling.
  const SortableMetricItem = ({ metric, isChecked, label }: { metric: string; isChecked: boolean; label: string }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: metric,
      disabled: !isChecked,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: isChecked ? "grab" : "default",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      opacity: isChecked ? 1 : 0.7,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...(isChecked ? { ...attributes, ...listeners } : {})}
        onClick={(e) => e.stopPropagation()}
      >
        <img src="/assets/icons/drag.svg" alt="Drag Icon" className="drag-handle" />
        <label className="checkbox-label custom-label">
          <input
            className="form-check-input checkbox-input"
            type="checkbox"
            checked={isChecked}
            onChange={() => toggleColumn(metric)}
            onClick={(e) => e.stopPropagation()}
            disabled={isLoading}
          />
          <span className="custom-checkbox-box"></span>
          <span className="metric-name-wrapper">{label}</span>
        </label>
      </div>
    );
  };

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const sensors = useSensors(pointerSensor);

  if (permittedMetrics.length === 0) return null;

  const selectedSet = new Set(selectedMetrics);
  const areDefaultsSelected =
    defaultMetrics.length > 0 &&
    selectedMetrics.length === defaultMetrics.length &&
    defaultMetrics.every((m) => selectedSet.has(m));

  const getLabel = (metric: string) =>
    metricLabels?.[metric] ?? formatMetricName(metric);

  return (
    <div className="columns-dropdown-container">
      <button
        ref={buttonRef}
        className="btn btn-outline-primary button-secondary customize-columns-btn"
        onClick={() => {
          calculateDropdownHeight();
          setDropdownOpen((prev) => !prev);
        }}
      >
        Customize Columns
      </button>
      <div
        className={`customize-columns-dropdown custom-dropdown-list ${dropdownOpen ? "open" : ""}`}
        ref={dropdownRef}
        style={{ maxHeight: `${dropdownMaxHeight}px` }}
      >
        <div className="columns-selection">
          {defaultMetrics.length > 0 && (
            <>
              <label className="checkbox-label custom-label inline-nowrap">
                <input
                  className="form-check-input checkbox-input"
                  type="checkbox"
                  checked={areDefaultsSelected}
                  onChange={(e) => (e.target.checked ? resetToDefaults() : clearAll())}
                  disabled={isLoading}
                />
                Default Columns
              </label>
              <div className="vertical-line-divider"></div>
            </>
          )}
          <label className="checkbox-label custom-label inline-nowrap">
            <input
              className="form-check-input checkbox-input"
              type="checkbox"
              checked={areAllSelected}
              onChange={(e) => (e.target.checked ? selectAll() : clearAll())}
              disabled={isLoading}
            />
            Select All
          </label>
        </div>
        <Input
          type="search"
          placeholder="Search metrics"
          value={searchTerm}
          onChange={handleSearchChange}
          className="column-search-input"
          disabled={false}
        />
        <div className="columns-list custom-scrollbar">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={metricOrder}
              strategy={verticalListSortingStrategy}
            >
              {filteredMetricsList.map((metric) => (
                <SortableMetricItem
                  key={`${tabId}-${exchangeId}-${metric}`}
                  metric={metric}
                  isChecked={selectedSet.has(metric)}
                  label={getLabel(metric)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        <div className="custmize-columns-footer">
          <button
            type="button"
            className="btn btn-outline-secondary button-secondary"
            onClick={clearAll}
            disabled={isLoading}
          >
            Clear All
          </button>
          <button
            type="button"
            className="btn btn-primary button-primary"
            onClick={() => setDropdownOpen(false)}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomizeColumns;
